import cn from "classnames";
import Image from "next/image";
import { useEffect, useState } from "react";

import { ImgBgResponsive } from "@/src/component/base/ImgBgResponsive";
import { BrokerProfileType } from "@/src/type";
import { FrontendTrackingService } from "@/src/service";
import { LoadFromCDN } from "@/src/util";

import styles from "./Contact.module.scss";

interface ContactProps {
  profile: BrokerProfileType.BrokerProfile;
  isMobile: boolean;
  shrink?: boolean;
  hasBrokerProfile?: boolean;
  contactLeftOnClick?: () => void;
  containerOnClick?: () => void;
}

function Contact({ profile, isMobile, shrink, hasBrokerProfile, contactLeftOnClick, containerOnClick }: ContactProps) {
  const [_shrink, setShrink] = useState<boolean>(!!shrink);

  useEffect(() => {
    if (shrink) {
      setShrink(true);
      return;
    }

    const timeout = setTimeout(() => {
      setShrink(false);
    }, 400);
    return () => clearTimeout(timeout);
  }, [shrink]);

  function trackContactClick() {
    if (!profile?.identityId) {
      return;
    }
    FrontendTrackingService.postTrackingLog({
      action: "click",
      actionId: profile.identityId,
      actionIdType: hasBrokerProfile ? "profile.identityId" : "promo.slug",
      actionMessage: hasBrokerProfile ? "contactBrokerButton" : "promoButtonClicked",
      triggeredFrom: "ProfileDrawer/component/Contact.tsx",
    });
  }

  const showSecondaryButton = profile.whatsApp || (!hasBrokerProfile && profile.brokerageWebsite);
  return (
    <div
      className={cn(
        styles.contact,
        isMobile ? styles.mobile : undefined,
        typeof containerOnClick === "function" ? styles.allPointerEvents : undefined
      )}
      onClick={containerOnClick}
    >
      <div
        className={cn(
          styles.contactLeft,
          contactLeftOnClick ? styles.pointer : undefined,
          isMobile && !hasBrokerProfile ? styles.noHandle : undefined
        )}
        onClick={contactLeftOnClick}
      >
        <div className={styles.headshot}>
          <div className={styles.headshotActiveDot} />
          <ImgBgResponsive
            lazy="eager"
            imageUrl={LoadFromCDN.imageSizeAndQuality(profile?.headshot?.data?.attributes?.url, 100, 500)}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
            }}
          />
        </div>
        {!_shrink && (
          <div className={styles.names}>
            <p className={styles.profileName}>
              {profile.firstName} {profile.lastName}
            </p>
            <p className={styles.brokerageName}>{profile.brokerageName}</p>
          </div>
        )}
      </div>
      {!_shrink && (
        <div className={styles.phone}>
          {showSecondaryButton && (
            <a
              className={styles.whatsAppBttn}
              // when not a broker profile, this becomes a promo banner
              // the brokerageWebsite: promo.button1Link becomes the new link for the whats app button
              href={hasBrokerProfile ? `https://wa.me/${profile.whatsApp}` : profile.brokerageWebsite}
              title="Link to Alternate Contact"
              onClick={trackContactClick}
              target={hasBrokerProfile ? "_alternateContact" : "_self"}
            >
              <Image
                src="/image/icon_whatsapp_dark.svg"
                width={18}
                height={18}
                alt="Alternate Contact Icon"
              />
            </a>
          )}
          {profile?.phoneNumber && (
            <a
              className={styles.phoneBttn}
              href={`tel:+${profile.countryCode ?? ""}${profile.phoneNumber}`}
              title="Link to Phone"
              onClick={trackContactClick}
            >
              <Image
                src="/image/icon_phone_light.svg"
                width={18}
                height={18}
                alt="Phone Number Icon"
              />
            </a>
          )}
        </div>
      )}
    </div>
  );
}

export default Contact;
